import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/auth-context';
import { useStreakQuery } from '../../hooks/use-daily';
import { formatRemaining, URGENT_THRESHOLD_MS } from '../../lib/countdown';
import { Icon } from '../shared/Icons';

// Repaint cadence. The display is whole minutes, so a second-by-second tick would re-render 60×
// for every visible change.
const TICK_MS = 30_000;

// A small persistent "your streak ends in …" pill, bottom-right of the app shell.
//
// Shown ONLY when there is something to lose: a live streak that today hasn't been answered for.
// Once the daily clip is answered there is no deadline worth watching, and at a streak of zero
// there is nothing at risk — a countdown in either case would be manufactured urgency rather than
// information. That's also why it disappears rather than turning into "0m".
//
// The deadline itself comes from the server (`resets_at` on GET /v1/me/streak): the streak resets
// at the FEDERATION's local midnight, so a client counting down to its own would be hours wrong
// for anyone away from home — and the daily loop, not the browser, owns that rule.
export function StreakCountdown() {
  const { role } = useAuth();
  const eligible = role === 'referee' || role === 'federation_admin';
  const { data } = useStreakQuery(eligible);
  const qc = useQueryClient();

  const [now, setNow] = useState(() => Date.now());

  const atRisk = Boolean(data && data.current_streak > 0 && !data.answered_today && data.resets_at);
  const msLeft = data?.resets_at ? new Date(data.resets_at).getTime() - now : null;
  const expired = atRisk && msLeft !== null && msLeft <= 0;

  // Only run a timer while a countdown is actually on screen — no interval ticking behind an
  // answered clip or a logged-out shell.
  useEffect(() => {
    if (!atRisk) return;
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [atRisk]);

  // The moment the deadline passes, this component's data is stale by definition: the server has
  // rolled the day over (new assignment, possibly a broken streak). Ask for the truth rather than
  // rendering our own guess at it.
  useEffect(() => {
    if (!expired) return;
    void qc.invalidateQueries({ queryKey: ['streak'] });
    void qc.invalidateQueries({ queryKey: ['daily-clip'] });
  }, [expired, qc]);

  if (!atRisk || msLeft === null) return null;
  const remaining = formatRemaining(msLeft);
  if (!remaining) return null;

  const urgent = msLeft <= URGENT_THRESHOLD_MS;
  const days = data?.current_streak ?? 0;

  return (
    // pointer-events-none so a pill floating over the page can never swallow a click meant for the
    // content beneath it. Not a live region: an assistive tech announcement every 30 seconds would
    // be nagging, so the label is read on demand instead.
    <div
      // z-30 keeps it above page content but BELOW the mobile nav drawer and its backdrop (z-40/50)
      // — at equal z-index this pill renders after the backdrop in the shell and would otherwise
      // float, undimmed, over an open drawer.
      className="pointer-events-none fixed bottom-4 right-4 z-30"
      aria-label={`${days}-day streak, ${remaining} left to answer today's clip`}
    >
      <div
        className={
          'flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] shadow-2 backdrop-blur ' +
          (urgent
            ? 'border-danger bg-danger-tint text-danger'
            : 'border-line-2 bg-surface/95 text-ink')
        }
      >
        <Icon.spark className={'h-3.5 w-3.5 flex-none ' + (urgent ? 'text-danger' : 'text-accent')} />
        <span className="font-medium">{remaining} left</span>
        {/* The explanatory tail is dropped on a phone — with it the pill ran most of the viewport
            width along the bottom edge. "3h 20m left" beside the streak spark still carries the
            urgency; the full sentence stays available to assistive tech via aria-label above. */}
        <span className={'hidden sm:inline ' + (urgent ? 'text-danger/80' : 'text-muted')}>
          to keep your {days}-day streak
        </span>
      </div>
    </div>
  );
}
